import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  Stack,
  TextField,
  Autocomplete,
  Checkbox,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ManagementSearchBar from '../shared/ManagementSearchBar'
import { getDefaultRange } from '../SalesReport/formatters'
import { formatOrganizationLabel } from '../../utils/organizationLabel'
import { organizationService } from '../../services'

const ORG_SEARCH_DEBOUNCE_MS = 300
const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />

function PromoReportFilters({ onFiltersChange }) {
  const [dateRange, setDateRange] = useState(getDefaultRange)
  const [selectedOrgs, setSelectedOrgs] = useState([])
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [search, setSearch] = useState('')
  const [organizations, setOrganizations] = useState([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgSearchText, setOrgSearchText] = useState('')
  const [orgInputValue, setOrgInputValue] = useState('')

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setOrgsLoading(true)
        const { items: orgItems } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 20,
          search: orgSearchText.trim() || undefined,
        })
        if (!cancelled) {
          setOrganizations(Array.isArray(orgItems) ? orgItems : [])
        }
      } catch (error) {
        console.error('Error fetching organizations:', error)
        if (!cancelled) setOrganizations([])
      } finally {
        if (!cancelled) setOrgsLoading(false)
      }
    }, ORG_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgSearchText])

  const orgOptions = useMemo(
    () =>
      organizations.map((org) => ({
        id: String(org.id),
        label: formatOrganizationLabel(org),
      })),
    [organizations],
  )

  const buildParams = useCallback(
    (overrides = {}) => {
      const orgs = 'orgs' in overrides ? overrides.orgs : selectedOrgs
      const orgIds = (orgs || []).map((org) => org.id).filter(Boolean).join(',')
      return {
        orgIds: orgIds || undefined,
        startDate: overrides.startDate ?? dateRange.startDate,
        endDate: overrides.endDate ?? dateRange.endDate,
        invoiceNumber:
          'invoiceNumber' in overrides
            ? overrides.invoiceNumber || undefined
            : invoiceNumber.trim() || undefined,
        search:
          'search' in overrides ? overrides.search || undefined : search || undefined,
      }
    },
    [selectedOrgs, dateRange, invoiceNumber, search],
  )

  const emit = useCallback(
    (overrides = {}) => {
      onFiltersChange(buildParams(overrides))
    },
    [buildParams, onFiltersChange],
  )

  useEffect(() => {
    onFiltersChange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    })
    // Load the default date range once. Later edits emit from their handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearchApply = useCallback(
    (value) => {
      setSearch(value)
      emit({ search: value || undefined })
    },
    [emit],
  )

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          sx={{ minWidth: 260, flex: 1 }}
          options={orgOptions}
          value={selectedOrgs}
          inputValue={orgInputValue}
          onChange={(_, value) => {
            const next = value || []
            setSelectedOrgs(next)
            setOrgInputValue('')
            setOrgSearchText('')
            emit({ orgs: next })
          }}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') {
              setOrgInputValue(value || '')
              setOrgSearchText(value || '')
            } else if (reason === 'clear') {
              setSelectedOrgs([])
              setOrgInputValue('')
              setOrgSearchText('')
              emit({ orgs: [] })
            }
          }}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={orgsLoading}
          filterOptions={(options) => options}
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props
            return (
              <li key={key} {...rest}>
                <Checkbox
                  icon={checkboxIcon}
                  checkedIcon={checkboxCheckedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Shop"
              placeholder={
                selectedOrgs.length === 0 ? 'All Shops' : 'Search shop'
              }
            />
          )}
        />
        <TextField
          size="small"
          type="date"
          label="Start date"
          value={dateRange.startDate}
          onChange={(e) => {
            const startDate = e.target.value
            setDateRange((prev) => ({ ...prev, startDate }))
            emit({ startDate })
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="End date"
          value={dateRange.endDate}
          onChange={(e) => {
            const endDate = e.target.value
            setDateRange((prev) => ({ ...prev, endDate }))
            emit({ endDate })
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Invoice number"
          value={invoiceNumber}
          onChange={(e) => {
            const value = e.target.value
            setInvoiceNumber(value)
            emit({ invoiceNumber: value.trim() })
          }}
          placeholder="Exact invoice number"
          sx={{ minWidth: 180 }}
        />
      </Stack>
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <ManagementSearchBar
          label="Search"
          placeholder="Search by article code or description"
          onApplySearchQuery={handleSearchApply}
          mb={0}
        />
      </Box>
    </Stack>
  )
}

export default memo(PromoReportFilters)
